/**
 * Business time.
 *
 * ONE implementation, imported by the Worker and by the client. Not two copies —
 * a date function that disagrees with itself across the wire is the worst kind
 * of bug, because both halves look right in isolation.
 *
 * ── Why this file exists ──────────────────────────────────────────────────────
 *
 * A Studio42 app usually serves ONE place. "Today" means today *there*, not
 * today in UTC and not today on the visitor's laptop. Getting this wrong has
 * already cost real money on this account, twice, in opposite directions:
 *
 *  - An events page computed `today` in UTC. Puerto Rico is UTC-4, so from
 *    20:00 AST the server had already rolled over — and tonight's event
 *    disappeared from the public page WHILE IT WAS HAPPENING.
 *  - An ordering flow computed fulfilment windows from the guest's device
 *    clock, so a visitor from California saw kitchen windows that had closed
 *    hours earlier.
 *
 * Both are the same bug: a date derived from the wrong clock.
 */

/**
 * An IANA timezone. Comes from `studio42.config.json`, never a literal in a
 * handler — an app that moves, or serves a second island, changes one line.
 */
export type Timezone = string

/**
 * Today, where the business is, as `YYYY-MM-DD`.
 *
 * ⚠️ EXACTLY ten characters. Callers compare it against date columns with
 * `>=` and `<=`, and a value carrying a time (`2026-08-28T00:00`) sorts BEFORE
 * every same-day row — which silently retires everything a day early. That is
 * not hypothetical; it is why this returns a string and not a Date.
 *
 * `en-CA` is not a stylistic choice: it is the locale whose short date format
 * is already ISO, so this needs no manual padding or reassembly.
 */
export function businessDate(tz: Timezone, at: Date | number = Date.now()): string {
  const date = typeof at === 'number' ? new Date(at) : at
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date)
}

/** The wall-clock parts where the business is. Minutes, not a Date — see above. */
export interface BusinessNow {
  /** `YYYY-MM-DD` */
  date: string
  /** 0–23, in the business timezone */
  hour: number
  /** 0–59 */
  minute: number
  /** Minutes since local midnight. The form most opening-hours comparisons want. */
  minutesSinceMidnight: number
  /** 0 = Sunday, matching Date.getDay() and every hours table on this account. */
  weekday: number
}

export function businessNow(tz: Timezone, at: Date | number = Date.now()): BusinessNow {
  const date = typeof at === 'number' ? new Date(at) : at

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short',
  }).formatToParts(date)

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''

  // `hour12: false` yields 24 for midnight in some runtimes and 00 in others.
  // Normalising here means callers never meet a 24 o'clock.
  const hour = Number(get('hour')) % 24
  const minute = Number(get('minute'))

  const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const weekday = WEEKDAYS.indexOf(get('weekday'))

  return {
    date: businessDate(tz, date),
    hour,
    minute,
    minutesSinceMidnight: hour * 60 + minute,
    weekday: weekday === -1 ? date.getUTCDay() : weekday,
  }
}

/** `YYYY-MM-DD`, `offsetDays` from today where the business is. Negative goes back. */
export function businessDatePlus(tz: Timezone, offsetDays: number, at: Date | number = Date.now()): string {
  const base = typeof at === 'number' ? at : at.getTime()
  /*
   * Shifting by whole days in UTC before formatting is deliberate. Adding to the
   * LOCAL hour instead would silently absorb a DST transition — which Puerto
   * Rico does not observe, but Detroit does, and this file is shared.
   */
  return businessDate(tz, base + offsetDays * 86_400_000)
}

/** Is this timestamp on the same business day as that one? */
export function sameBusinessDay(tz: Timezone, a: Date | number, b: Date | number): boolean {
  return businessDate(tz, a) === businessDate(tz, b)
}

/**
 * The UTC instant of local midnight starting a business day.
 *
 * For range queries against a timestamp column: everything on 2026-08-28 in
 * Puerto Rico is `>= startOfBusinessDay('2026-08-28')` and
 * `< startOfBusinessDay('2026-08-29')`.
 *
 * Found by search rather than arithmetic because a fixed offset is wrong for
 * any zone with DST, and hard-coding -4 for Puerto Rico is exactly the shortcut
 * that breaks the day this code is reused for a mainland business.
 */
export function startOfBusinessDay(tz: Timezone, date: string): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    throw new Error(`startOfBusinessDay expects YYYY-MM-DD, got "${date}"`)
  }
  const noonUtc = Date.parse(`${date}T12:00:00Z`)
  // Local midnight is within ±14h of noon UTC for every real zone. Walk back an
  // hour at a time until the business date changes, then forward to the boundary.
  for (let h = 0; h <= 28; h++) {
    const candidate = noonUtc - h * 3_600_000
    if (businessDate(tz, candidate) !== date) {
      // The previous hour was still the right day; find the minute boundary.
      let lo = candidate
      let hi = candidate + 3_600_000
      while (hi - lo > 60_000) {
        const mid = lo + Math.floor((hi - lo) / 2 / 60_000) * 60_000
        if (mid === lo) break
        if (businessDate(tz, mid) === date) hi = mid
        else lo = mid
      }
      return hi
    }
  }
  return noonUtc
}

/** Formatted for a person, in the business timezone. Never the viewer's zone. */
export function formatBusinessTime(
  tz: Timezone,
  at: Date | number,
  locale = 'en-US',
  options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' },
): string {
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: tz }).format(
    typeof at === 'number' ? new Date(at) : at,
  )
}
